import React, { useState } from 'react';
import { X, Mail, Lock, User, Building, Globe, ArrowRight, CheckCircle, Sparkles, Rocket, Zap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
// RadioGroup removed - using simple buttons instead
import { toast } from 'sonner';
import axios from 'axios';
import { setAuth } from '@/lib/auth';
import GoogleSignInButton from '@/components/GoogleSignInButton';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const formatErrorMessage = (error, defaultMessage) => {
  if (typeof error === 'string') return error;
  if (error?.response?.data) {
    const errorData = error.response.data;
    if (Array.isArray(errorData.detail)) {
      return errorData.detail.map(err => err.msg || err.message || 'Validation error').join(', ');
    }
    if (typeof errorData.detail === 'string') return errorData.detail;
    if (typeof errorData.detail === 'object' && errorData.detail.msg) return errorData.detail.msg;
    if (errorData.message) return errorData.message;
  }
  return error?.message || defaultMessage;
};

export default function AuthModal({ onClose, onSuccess }) {
  const [activeTab, setActiveTab] = useState('login');
  const [loading, setLoading] = useState(false);
  
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  
  const [signupEmail, setSignupEmail] = useState('');
  const [signupPassword, setSignupPassword] = useState('');
  const [signupName, setSignupName] = useState('');
  const [signupRole, setSignupRole] = useState('participant');
  const [companyName, setCompanyName] = useState('');
  const [companyWebsite, setCompanyWebsite] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!loginEmail || !loginPassword) {
      toast.error('Please fill in all fields');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/auth/login`, {
        email: loginEmail,
        password: loginPassword,
      });

      const { session_token, ...user } = response.data;
      setAuth(session_token, user);
      
      if (!user.email_verified) {
        toast.warning('Please verify your email to continue');
        onClose();
        window.location.href = '/verification-required';
        return;
      }
      
      toast.success(`Welcome back, ${user.name}!`);
      onClose();
      setTimeout(() => {
        window.location.href = '/dashboard';
      }, 100);
    } catch (error) {
      console.error('Login error:', error);
      toast.error(formatErrorMessage(error, 'Login failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    if (!signupEmail || !signupPassword || !signupName) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (signupRole === 'organizer' && !companyName) {
      toast.error('Company name is required for organizers');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/auth/signup`, {
        email: signupEmail,
        password: signupPassword,
        name: signupName,
        role: signupRole,
        company_name: signupRole === 'organizer' ? companyName : undefined,
        company_website: signupRole === 'organizer' ? companyWebsite : undefined,
      });

      const { session_token, ...user } = response.data;
      setAuth(session_token, user);
      
      toast.success(`Welcome to Hackov8, ${user.name}! Please verify your email.`);
      onClose();
      window.location.href = '/verification-required';
    } catch (error) {
      console.error('Signup error:', error);
      toast.error(formatErrorMessage(error, 'Signup failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md relative">
        
        {/* Close Button */}
        <button 
          onClick={onClose} 
          className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors"
          disabled={loading}
        >
          <X className="w-5 h-5 text-white" />
        </button>

        {/* Hero Header with Logo */}
        <div className="relative bg-gradient-to-br from-blue-600 via-blue-500 to-cyan-500 px-6 pt-8 pb-12 text-white rounded-t-3xl">
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-10 rounded-t-3xl overflow-hidden">
            <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.1)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.1)_1px,transparent_1px)] bg-[size:20px_20px]"></div>
          </div>
          
          {/* Floating Elements */}
          <div className="absolute top-4 left-4 w-20 h-20 bg-white/10 rounded-full blur-2xl"></div>
          <div className="absolute bottom-4 right-4 w-32 h-32 bg-cyan-400/20 rounded-full blur-3xl"></div>
          
          <div className="relative flex flex-col items-center text-center">
            {/* Logo */}
            <div className="mb-3 p-3 bg-white rounded-2xl shadow-xl">
              <img 
                src="https://customer-assets.emergentagent.com/job_50845b9f-14b0-452c-b665-fa440207483a/artifacts/opizci2q_hack.png" 
                alt="Hackov8" 
                className="h-14 sm:h-16 w-auto"
              />
            </div>
            
            {/* Tagline */}
            <div className="flex items-center gap-2 mb-1">
              <Rocket className="w-4 h-4 text-cyan-200" />
              <span className="text-base font-semibold">Join the Innovation</span>
              <Zap className="w-4 h-4 text-yellow-300" />
            </div>
            <p className="text-white/90 text-xs font-medium">
              50,000+ developers building the future
            </p>
          </div>
        </div>

        {/* Form Section - Overlapping Card Effect */}
        <div className="relative -mt-6 mx-4 mb-4">
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-5">
            
            {/* Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-2 mb-5 bg-gray-100 p-1 rounded-xl">
                <TabsTrigger 
                  value="login" 
                  className="rounded-lg py-2 text-sm font-bold data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-600 data-[state=active]:to-cyan-500 data-[state=active]:text-white data-[state=active]:shadow-md transition-all"
                  data-testid="login-tab"
                >
                  Login
                </TabsTrigger>
                <TabsTrigger 
                  value="signup" 
                  className="rounded-lg py-2 text-sm font-bold data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-600 data-[state=active]:to-cyan-500 data-[state=active]:text-white data-[state=active]:shadow-md transition-all"
                  data-testid="signup-tab"
                >
                  Sign Up
                </TabsTrigger>
              </TabsList>

              {/* Login Tab */}
              <TabsContent value="login" className="space-y-4 mt-0">
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="login-email" className="text-gray-700 font-semibold text-sm">
                      Email Address
                    </Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-blue-500" />
                      <Input
                        id="login-email"
                        type="email"
                        value={loginEmail}
                        onChange={(e) => setLoginEmail(e.target.value)}
                        placeholder="you@example.com"
                        className="h-11 pl-11 border-2 border-gray-200 focus:border-blue-500 rounded-xl transition-all"
                        disabled={loading}
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="login-password" className="text-gray-700 font-semibold text-sm">
                      Password
                    </Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-blue-500" />
                      <Input
                        id="login-password"
                        type="password"
                        value={loginPassword}
                        onChange={(e) => setLoginPassword(e.target.value)}
                        placeholder="Enter your password"
                        className="h-11 pl-11 border-2 border-gray-200 focus:border-blue-500 rounded-xl transition-all"
                        disabled={loading}
                        required
                      />
                    </div>
                  </div>

                  <div className="text-right">
                    <a 
                      href="/forgot-password" 
                      className="text-sm text-blue-600 hover:text-blue-700 font-semibold hover:underline"
                    >
                      Forgot Password?
                    </a>
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full h-12 bg-gradient-to-r from-blue-600 via-blue-500 to-cyan-500 hover:from-blue-700 hover:via-blue-600 hover:to-cyan-600 text-white rounded-xl font-bold shadow-lg shadow-blue-500/30 hover:shadow-xl hover:shadow-blue-500/40 transition-all flex items-center justify-center gap-2 text-base"
                    data-testid="login-submit-btn"
                  >
                    {loading ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        Logging in...
                      </>
                    ) : (
                      <>
                        Login to Dashboard
                        <ArrowRight className="w-5 h-5" />
                      </>
                    )}
                  </Button>

                  <div className="relative my-4">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-gray-200"></div>
                    </div>
                    <div className="relative flex justify-center text-xs">
                      <span className="px-3 bg-white text-gray-400">or continue with</span>
                    </div>
                  </div>

                  <GoogleSignInButton />
                </form>
              </TabsContent>

              {/* Signup Tab */}
              <TabsContent value="signup" className="space-y-3 mt-0">
                <form onSubmit={handleSignup} className="space-y-3">
                  <div className="space-y-1">
                    <Label htmlFor="signup-name" className="text-gray-700 font-semibold text-sm">
                      Full Name
                    </Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-blue-500" />
                      <Input
                        id="signup-name"
                        type="text"
                        value={signupName}
                        onChange={(e) => setSignupName(e.target.value)}
                        placeholder="John Doe"
                        className="h-10 pl-11 border-2 border-gray-200 focus:border-blue-500 rounded-xl transition-all text-sm"
                        disabled={loading}
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-1">
                    <Label htmlFor="signup-email" className="text-gray-700 font-semibold text-sm">
                      Email Address
                    </Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-blue-500" />
                      <Input
                        id="signup-email"
                        type="email"
                        value={signupEmail}
                        onChange={(e) => setSignupEmail(e.target.value)}
                        placeholder="you@example.com"
                        className="h-10 pl-11 border-2 border-gray-200 focus:border-blue-500 rounded-xl transition-all text-sm"
                        disabled={loading}
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-1">
                    <Label htmlFor="signup-password" className="text-gray-700 font-semibold text-sm">
                      Password
                    </Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-blue-500" />
                      <Input
                        id="signup-password"
                        type="password"
                        value={signupPassword}
                        onChange={(e) => setSignupPassword(e.target.value)}
                        placeholder="Create a strong password"
                        className="h-10 pl-11 border-2 border-gray-200 focus:border-blue-500 rounded-xl transition-all text-sm"
                        disabled={loading}
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-gray-700 font-semibold text-sm">I want to</Label>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        onClick={() => setSignupRole('participant')}
                        className={`flex items-center justify-center gap-2 p-2.5 border-2 rounded-xl cursor-pointer transition-all ${signupRole === 'participant' ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 hover:border-blue-300 text-gray-600'}`}
                        data-testid="role-participant-btn"
                      >
                        <Rocket className="w-4 h-4" />
                        <span className="font-bold text-sm">Participate</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setSignupRole('organizer')}
                        className={`flex items-center justify-center gap-2 p-2.5 border-2 rounded-xl cursor-pointer transition-all ${signupRole === 'organizer' ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 hover:border-blue-300 text-gray-600'}`}
                        data-testid="role-organizer-btn"
                      >
                        <Building className="w-4 h-4" />
                        <span className="font-bold text-sm">Organize</span>
                      </button>
                    </div>
                  </div>

                  {signupRole === 'organizer' && (
                    <div className="space-y-2 p-3 bg-blue-50 rounded-xl border border-blue-100">
                      <div className="space-y-1">
                        <Label htmlFor="company-name" className="text-gray-700 font-semibold text-xs">
                          Company/Organization
                        </Label>
                        <Input
                          id="company-name"
                          type="text"
                          value={companyName}
                          onChange={(e) => setCompanyName(e.target.value)}
                          placeholder="Your company name"
                          className="h-9 text-sm border-2 border-gray-200 focus:border-blue-500 rounded-lg bg-white"
                          disabled={loading}
                          required
                        />
                      </div>
                    </div>
                  )}

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full h-12 bg-gradient-to-r from-blue-600 via-blue-500 to-cyan-500 hover:from-blue-700 hover:via-blue-600 hover:to-cyan-600 text-white rounded-xl font-bold shadow-lg shadow-blue-500/30 hover:shadow-xl hover:shadow-blue-500/40 transition-all flex items-center justify-center gap-2 text-base"
                    data-testid="signup-submit-btn"
                  >
                    {loading ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        Creating...
                      </>
                    ) : (
                      <>
                        Create Account
                        <CheckCircle className="w-5 h-5" />
                      </>
                    )}
                  </Button>

                  <div className="relative my-3">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-gray-200"></div>
                    </div>
                    <div className="relative flex justify-center text-xs">
                      <span className="px-3 bg-white text-gray-400">or</span>
                    </div>
                  </div>

                  <GoogleSignInButton />
                </form>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>
    </div>
  );
}
